import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Calendar, RefreshCw, Plus, Edit2, Trash2, RotateCcw } from 'lucide-react';
import { appointmentsAPI } from '../../lib/api.js';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import { isAdminOrAbove } from '../../lib/rbac.js';

function HolidaysPage() {
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['appointments-holidays'],
    queryFn: () => appointmentsAPI.getHolidays(),
    // Removed refetchInterval to prevent unnecessary API calls
    // refetchInterval: 60000,
  });

  const holidays = data?.data?.holidays || data?.holidays || [];

  const [showCreate, setShowCreate] = useState(false);
  const [formData, setFormData] = useState({
    date: '',
    name: '',
    type: 'national',
    description: '',
    active: true,
  });

  const createMutation = useMutation({
    mutationFn: (payload) => appointmentsAPI.createHoliday(payload),
    onSuccess: () => {
      queryClient.invalidateQueries(['appointments-holidays']);
      setShowCreate(false);
      setFormData({ date: '', name: '', type: 'national', description: '', active: true });
    },
  });

  const rescheduleMutation = useMutation({
    mutationFn: (holidayDate) => appointmentsAPI.rescheduleByHolidayDate(holidayDate),
    onSuccess: () => {
      // No-op; backend processes rescheduling. Consider toast in future.
    },
  });

  return (
    <div className="h-full px-6 py-6 overflow-y-auto dark:text-white">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Holidays</h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg">View upcoming and active holidays</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdminOrAbove() && (
            <Button onClick={() => setShowCreate(true)} className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white">
              <Plus className="h-4 w-4" />
              New Holiday
            </Button>
          )}
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-teal-600" />
          <span className="font-medium">Active Holidays</span>
        </div>

        {isLoading ? (
          <div className="p-6">
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            </div>
          </div>
        ) : holidays.length === 0 ? (
          <div className="p-6 text-center text-gray-600 dark:text-gray-400">No holidays found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {holidays.map((h) => (
                  <tr key={`${h.date}-${h.name}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{h.date}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{h.name}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300">
                        {h.type || 'national'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{h.description || '-'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        {isAdminOrAbove() && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => rescheduleMutation.mutate(h.date)}
                            title="Auto-reschedule appointments for this holiday"
                          >
                            <RotateCcw className="h-4 w-4" />
                            Reschedule
                          </Button>
                        )}
                        {/* Edit/Deactivate require backend endpoints; leaving placeholders disabled */}
                        <Button variant="outline" size="sm" disabled title="Edit requires backend endpoint">
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="danger" size="sm" disabled title="Deactivate requires backend endpoint">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Create Holiday</h2>
              <Button variant="outline" size="sm" onClick={() => setShowCreate(false)}>×</Button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                createMutation.mutate(formData);
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
                  <Input type="date" value={formData.date} onChange={(e) => setFormData((p) => ({ ...p, date: e.target.value }))} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData((p) => ({ ...p, type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    <option value="national">National</option>
                    <option value="government">Government</option>
                    <option value="regional">Regional</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <Input value={formData.name} onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  placeholder="Optional"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="active"
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData((p) => ({ ...p, active: e.target.checked }))}
                  className="h-4 w-4"
                />
                <label htmlFor="active" className="text-sm text-gray-700 dark:text-gray-300">Active</label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" type="button" onClick={() => setShowCreate(false)}>Cancel</Button>
                <Button type="submit" disabled={createMutation.isPending} className="bg-teal-600 hover:bg-teal-700 text-white">
                  {createMutation.isPending ? 'Saving...' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default HolidaysPage;


